/**
 * Request object for retrieving door options
 */
export class LockDesignerDoorTypeRequest {
    /**
     * Door type data to return
     */
    doorTypeOption: DoorTypeOption;

    /**
     * User's current selections
     */
    selections: LockDesignerSelections;
}

/**
 * Response object returned when retrieving door options
 */
export class LockDesignerDoorTypeResponse {
    doorTypeOption: DoorTypeOption;
    backToRoute: string;
    compatibilityModal: LockDesignerCompatibilityModal;
    selections: LockDesignerSelections;
    componentGroups: LockDesignerComponentGroup[];
}

/**
 * This object is used to pass in user selections, to determine which selections
 * are compatible with each other.
 */
export class LockDesignerSelectionsRequest {
    /**
     * User's current selections
     */
    selections: LockDesignerSelections;

    /**
     * Should be populated with the field that was changed by current selection
     */
    optionChanged: OptionChanged;

    /**
     * Indicates if viewing interior/exterior
     */
    exterior: boolean;
}

/**
 * This object is returned as the results of selections that were updated. If any of the
 * user selections were incompatible, this model will contain updated properties so all
 * user selections are again compatible.
 */
export class LockDesignerSelectionsResponse {
    /**
     * Updated user selections
     */
    selections: LockDesignerSelections;

    /**
     * Component groups
     */
    componentGroups: LockDesignerComponentGroup[];
}

/**
 * This object is used to send the user's answers for the Door Fit Finder quiz. The
 * selections are validated and the response will indicate which Door Prep and Function
 * to set for future requests.
 */
export class LockDesignerDoorFitFinderRequest {
    /**
     * Existing hardware
     */
    existingHardware: DoorFitFinderExistingHardware;

    /**
     * Lock Type - can be NULL
     */
    lock: DoorFitFinderLock;

    /**
     * Door Edge
     */
    doorEdge: DoorFitFinderDoorEdge;
}

/**
 * This object is returned as the results of the Door Fit Finder API
 * and returns the Door Prep and Function to set for future requests.
 */
export class LockDesignerDoorFitFinderResponse {
    /**
     * Selections
     */
    selections: LockDesignerSelections;

    /**
     * Confirm door edge selection
     */
    confirmDoorEdgeSelection: boolean;
}

/**
 * Request object for the filters API
 */
export class LockDesignerFiltersRequest {
    filters: FilterCategoryRequestValue[];
    selections: LockDesignerSelections;
}

/**
 * Response object for the filters API
 */
export class LockDesignerFiltersResponse {
    filters: FilterCategory[];
}

/**
 * Request object for products API
 */
export class LockDesignerProductsRequest {
    productType: LockDesignerProductType;
    selections: LockDesignerSelections;
    filters: FilterCategoryRequestValue[];
    filterOptions: FilterOptions;
    searchTerm: string;
}

/**
 * Response object for products API
 */
export class LockDesignerProductsResponse {
    backToRoute: string;
    selections: LockDesignerSelections;
    componentGroups: LockDesignerComponentGroup[];
}

/**
 * Share request
 */
export class LockDesignerShareRequest {
    shareUrl: string;
    emailAddress: string;
}

/**
 * Compatibility Modal
 */
export class LockDesignerCompatibilityModal {
    title: string;
    message: string;
}

/**
 * Component Group
 */
export class LockDesignerComponentGroup {
    title: string;
    cTAText: string;
    cTARoute: string;
    isHeader: boolean;
    components: LockDesignerComponent[];
}

/**
 * Generic component
 */
export interface LockDesignerComponent {
    id: string;
    displayName: string;
    description: string;
    vueComponent: VueComponent;
    imageUrl: string;
    route: string;
    isSelected: boolean;
    fieldId: string;
    compatibilityMessage: string;
    tooltip: string;
    productDetailPageUrl: string;
}

/**
 * Finishes component
 */
export interface LockDesignerFinishesComponent {
    vueComponent: VueComponent;
    syncedFinish: string;
    productFinish: string;
    finishes: LockDesignerComponent[];
}

/**
 * Product filter component
 */
export interface LockDesignerProductFiltersComponent {
    vueComponent: VueComponent;
    productCount: number;
    filters: FilterCategory[];
}

/**
 * Products component
 */
export interface LockDesignerProductsComponent {
    vueComponent: VueComponent;
    fieldId: string;
    products: LockDesignerProduct[];
}

/**
 * Base interface for component
 */
export interface LockDesignerComponent { }

/**
 * Product
 */
export interface LockDesignerProduct extends LockDesignerOption {
    assetType: LockDesignerAssetType | null;
    functions: string[];
    style: string;
}

/**
 * Search result for Door Type options
 */
export class LockDesignerOption {
    /**
     * Primary key or slug of item
     */
    id: string;

    /**
     * Display name
     */
    displayName: string;

    /**
     * Description
     */
    description: string;

    /**
     * Image Url
     */
    imageUrl: string;

    /**
     * Flag indicating if this option is currently selected
     */
    isSelected: boolean;

    /**
     * Subcategory
     */
    subcategoryType: string;

    /**
     * Compatibility information about this option
     */
    compatibility: LockDesignerCompatibility;
}

/**
 * User selections for Lock Designer
 */
export class LockDesignerSelections {
    /**
     * Selected function
     */
    function: LockDesignerOption;

    /**
     * Selected door prep
     */
    doorPrep: LockDesignerOption;

    /**
     * Selected door style
     */
    doorStyle: LockDesignerOption;

    /**
     * Selected outside handle
     */
    outsideHandle: LockDesignerOption;

    /**
     * Selected outside escutcheon
     */
    outsideEscutcheon: LockDesignerOption;

    /**
     * Selected outside privacy bolt
     */
    outsidePrivacyBolt: LockDesignerOption;

    /**
     * Selected inside handle
     */
    insideHandle: LockDesignerOption;

    /**
     * Selected inside escutcheon
     */
    insideEscutcheon: LockDesignerOption;

    /**
     * Selected inside privacy bolt
     */
    insidePrivacyBolt: LockDesignerOption;

    /**
     * Selected inside turn knob backplate
     */
    insideBackplate: LockDesignerOption;

    /**
     * Flag indicating if Door Fit Finder is turned on
     */
    doorFitFinderEnabled: boolean;

    /**
     * Scene7 image for composite image
     */
    scene7ImageUrl: string;

    /**
     * Finish name to restrict all product selections
     */
    syncedFinish: string;
}

/**
 * Visibility properties, which indicate which properties are visible in the UI
 */
export class LockDesignerVisibleSelections {
    /**
     * Outside Handle Visible
     */
    outsideHandleVisible: boolean;

    /**
     * Outside Escutcheon Visible
     */
    outsideEscutcheonVisible: boolean;

    /**
     * Outside Deadbolt Visible
     */
    outsideDeadboltVisible: boolean;

    /**
     * Outside Turn Piece Visible
     */
    outsideTurnPieceVisible: boolean;

    /**
     * Outside Trim Ring Visible
     */
    outsideTrimRingVisible: boolean;

    /**
     * Inside Handle Visible
     */
    insideHandleVisible: boolean;

    /**
     * Inside Escutcheon Visible
     */
    insideEscutcheonVisible: boolean;

    /**
     * Inside Deadbolt Visible
     */
    insideDeadboltVisible: boolean;

    /**
     * Inside Turn Piece Visible
     */
    insideTurnPieceVisible: boolean;

    /**
     * Inside Trim Ring Visible
     */
    insideTrimRingVisible: boolean;
}

/**
 * Used to indicate if a selection has compatibility issues with current selections
 */
export class LockDesignerCompatibility {
    compatibilityMessage: string;
    doorFitFinderMessage: string;
    isCompatible: boolean;
}

/**
 * Simplied model that inculdes on the facet and corresponding values to filter by
 */
export class FilterCategoryRequestValue {
    name: string;
    values: string[];
    displayExpanded: boolean | null;
}

/**
 * Lock Designer user
 */
export class LockDesignerUser {
    emailAddress: string;
}

/**
 * Registration request
 */
export class LockDesignerUserRegisterRequest extends LockDesignerUser {
    password: string;
    confirmPassword: string;
}

/**
 * Registration response
 */
export class LockDesignerUserRegisterResponse {
    authToken: string;
}

/**
 * Login request
 */
export class LockDesignerUserLoginRequest extends LockDesignerUser {
    password: string;
}

/**
 * Login resopnse
 */
export class LockDesignerUserLoginResponse extends LockDesignerUserRegisterResponse { }

/**
 * Forgot password request
 */
export class LockDesignerUserForgotPasswordRequest extends LockDesignerUser { }

/**
 * Reset password request
 */
export class LockDesignerUserResetPasswordRequest extends LockDesignerUser {
    vk: string;
    password: string;
    confirmPassword: string;
}

/**
 * Reset password response
 */
export class LockDesignerUserResetPasswordResponse extends LockDesignerUserRegisterResponse { }

/**
 * Filter category that's returned by filters API
 */
export class FilterCategory {
    title: string;
    slug: string;
    description: string;
    displayType: string;
    hasFilters: boolean;
    displayExpanded: boolean;
    isSingleSelect: boolean;
    filterValues: FilterValue[];
}

/**
 * Filter value
 */
export class FilterValue {
    title: string;
    displayName: string;
    description: string;
    thumbnail: string;
    showName: boolean;
    numResults: number;
    isSelected: boolean;
    hide: boolean;
}

/**
 * Pagination request
 */
export class FilterOptions {
    constructor(pageNumber: number, resultsPerPage: number) {
        this.pageNumber = pageNumber;
        this.resultsPerPage = resultsPerPage;
    }

    /**
     * Current page number (defaults to 1)
     */
    pageNumber: number | null;

    /**
     * Number of results to return per page
     */
    resultsPerPage: number | null;
}

/**
 * Wishlist items request
 */
export class LockDesignerWishlistItemsRequest {
    wishlistName: string;
}

/**
 * Wishlist items response
 */
export class LockDesignerWishlistItemsResponse {
    selectedWishlist: string;
    items: LockDesignWishlistItem[];
    wishlists: string[];
}

/**
 * Wishlist items to add request
 */
export class LockDesignerWishlistItemsToAddRequest {
    selections: LockDesignerSelections;
    /**
     * Whishlist name can be NULL when getting initial list of items to add
     */
    wishlistName: string;
}

/**
 * Wishlist items to add response
 */
export class LockDesignerWishlistItemsToAddResponse {
    components: LockDesignerComponent[];
    wishlists: string[];
}

/**
 * Wishlist item
 */
export class LockDesignWishlistItem {
    id: number;
    displayName: string;
    finishDisplayName: string;
    imageUrl: string;
}

/**
 * Pagination response
 */
export class PagingInfo {
    /**
     * Current page number (defaults to 1)
     */
    pageNumber: number;

    /**
     * Number of results per page
     */
    resultsPerPage: number;

    /**
     * Total number of pages
     */
    totalPages: number;

    /**
     * Total number of results
     */
    totalProducts: number;
}

/**
 * Error messages
 */
export class ErrorDetail {
    /**
     * Indicates if request was successful
     */
    isError: boolean | null;

    /**
     * Server generated unique Id
     */
    errorId: string | null;

    /**
     * Collection of error messages
     */
    messages: string[];
}

/**
 * Door type option to retrieve
 */
export enum DoorTypeOption {
    Function,
    DoorPrep,
    DoorStyle,
}

/**
 * Property that can changed by user
 */
export enum OptionChanged {
    None,
    Defaults,
    Function,
    DoorPrep,
    DoorStyle,
    Handle,
    Escutcheon,
    PrivacyBolt,
    Deadbolt,
    TurnKnob,
    TurnPiece,
    TrimRing,
}

/**
 * Product type
 */
export enum LockDesignerProductType {
    Handle,
    Escutcheon,
    PrivacyBolt,
    Deadbolt,
    TurnKnob,
    BoltTurnPiece,
    TurnBackplate,
    None,
    Unknown
}

/**
 * Door Fit Finder - Existing Hardware
 */
export enum DoorFitFinderExistingHardware {
    KnobOrLever,
    Handleset,
    NewDoor,
}

/**
 * Door Fit Finder - Lock Type
 */
export enum DoorFitFinderLock {
    PushButton,
    Deadbolt,
    None,
}

/**
 * Door Fit Finder - Door Edge
 */
export enum DoorFitFinderDoorEdge {
    TubularSingle,
    TubularDouble,
    Mortise,
    NoHolesNewDoor,
}

/**
 * Vue Components
 */
export enum VueComponent {
    ComponentSelector,
    DoorFitFinderSelection,
    Selection,
    Selections,
    DoorType,
    Function,
    DoorPrep,
    DoorStyle,
    Handle,
    Escutcheon,
    PrivacyBolt,
    Deadbolt,
    TurnKnob,
    TurnBackplate,
    QuickShip,
    StandardShipping,
    ProductFinishes,
    ProductFilters,
    Products,
}

/**
 * Asset type
 */
export enum LockDesignerAssetType {
    Door,
    Handle,
    Knob,
    Lever,
    Handleset,
    Escutcheon,
    StandardRose,
    SmallBoreRose,
    NineInchRose,
    LargeRose,
    FiveEighthRose,
    LargeEscutcheonSmallBoreRose,
    Deadbolt,
    BoltTurnPiece,
    TurnKnob,
    TurnPieceSet,
    Backplate,
}
